/**
 * Developer note:
 * - localStorage key: `hallv-menu-config` (exported as MENU_CONFIG_STORAGE_KEY)
 * - persisted shape (canonical): MenuConfig = { language, theme, fontFamily }
 * - initial load + safe defaults: readMenuConfig() (persists defaults when empty/corrupt)
 * - DOM application helpers: applyMenuConfigToDom() (lang + font), applyThemeToDom() (theme classes)
 */

export const MENU_CONFIG_STORAGE_KEY = "hallv-menu-config";

export type ThemeMode = "light" | "dark";
export type MenuFontFamily = "sans" | "serif";

export interface MenuConfig {
	/**
	 * Language/country code used by this app:
	 * - "en" means base (no filename suffix)
	 * - non-English are UPPERCASE suffixes like "IDN" (used in *_IDN.html)
	 */
	language: string;
	theme: ThemeMode;
	fontFamily: MenuFontFamily;
}

export interface MenuLanguageOption {
	code: string;
	label: string;
}

/**
 * Minimal list; extend as more localized HTML assets exist.
 * (The app's URL redirect logic only works if the corresponding *_XXX.html files exist.)
 */
export const MENU_LANGUAGES: readonly MenuLanguageOption[] = [
	{ code: "en", label: "English" },
	{ code: "BN", label: "Bengali" },
	{ code: "ES", label: "Spanish" },
	{ code: "FR", label: "French" },
	{ code: "DE", label: "German" },
	{ code: "IDN", label: "Indonesian" },
	{ code: "HI", label: "Hindi" },
	{ code: "AR", label: "Arabic" },
	{ code: "RU", label: "Russian" },
	{ code: "HE", label: "Hebrew" },
	{ code: "JA", label: "Japanese" },
	{ code: "ZH", label: "Chinese" },
	{ code: "KO", label: "Korean" },
	{ code: "TH", label: "Thailand" },
	{ code: "PA", label: "Punjabi" },
	{ code: "TR", label: "Turkish" },
	{ code: "VI", label: "Vietnamese" },
];

const DEFAULT_SITE_LANGUAGE = "en";
const PUBLIC_PATH_PREFIX = "/public";

export const normalizeMenuLanguage = (lang: string): string => {
	const raw = (lang || "").trim();
	if (!raw) return DEFAULT_SITE_LANGUAGE;

	const lower = raw.toLowerCase();
	if (
		lower === "id" ||
		lower === "idn" ||
		lower === "indonesia" ||
		lower === "indonesian"
	) {
		return "IDN";
	}

	if (
		lower === "hi" ||
		lower === "hindi"
	) {
		return "HI";
	}

	if (
		lower === "ar" ||
		lower === "ara" ||
		lower === "arabic"
	) {
		return "AR";
	}

	if (
		lower === "ru" ||
		lower === "rus" ||
		lower === "russian"
	) {
		return "RU";
	}

	if (
		lower === "he" ||
		lower === "heb" ||
		lower === "hebrew"
	) {
		return "HE";
	}

	if (
		lower === "ja" ||
		lower === "jpn" ||
		lower === "japanese"
	) {
		return "JA";
	}

	if (
		lower === "zh" ||
		lower === "zho" ||
		lower === "chinese"
	) {
		return "ZH";
	}

	if (
		lower === "ko" ||
		lower === "kor" ||
		lower === "korean"
	) {
		return "KO";
	}

	if (
		lower === "th" ||
		lower === "tha" ||
		lower === "thailand" ||
		lower === "thai"
	) {
		return "TH";
	}

	if (
		lower === "bn" ||
		lower === "ben" ||
		lower === "bengali"
	) {
		return "BN";
	}

	if (
		lower === "es" ||
		lower === "spa" ||
		lower === "spanish" ||
		lower === "español"
	) {
		return "ES";
	}

	if (
		lower === "fr" ||
		lower === "fra" ||
		lower === "french" ||
		lower === "français"
	) {
		return "FR";
	}

	if (
		lower === "de" ||
		lower === "ger" ||
		lower === "german" ||
		lower === "deutsch"
	) {
		return "DE";
	}

	if (
		lower === "pa" ||
		lower === "pan" ||
		lower === "punjabi" ||
		lower === "panjabi"
	) {
		return "PA";
	}

	if (
		lower === "tr" ||
		lower === "tur" ||
		lower === "turkish" ||
		lower === "türkçe" ||
		lower === "turkce"
	) {
		return "TR";
	}

	if (
		lower === "vi" ||
		lower === "vie" ||
		lower === "vietnamese" ||
		lower === "vietnam"
	) {
		return "VI";
	}

	const upper = raw.toUpperCase();
	if (upper === "EN" || upper === "EN-US" || upper === "EN-GB") {
		return DEFAULT_SITE_LANGUAGE;
	}

	// Accept 2-3 letter language/country codes (e.g. IDN)
	if (/^[A-Z]{2,3}$/.test(upper)) return upper;

	// Unknown format -> default to English so we don't generate invalid suffix URLs.
	return DEFAULT_SITE_LANGUAGE;
};

export const detectPreferredThemeFromSystem = (): ThemeMode => {
	if (window.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
		return "dark";
	}
	return "light";
};

const stripPublicPrefix = (
	pathname: string,
): { prefix: "" | "/public"; rest: string } => {
	const hasPublicPrefix =
		pathname === PUBLIC_PATH_PREFIX ||
		pathname.startsWith(`${PUBLIC_PATH_PREFIX}/`);
	if (!hasPublicPrefix) return { prefix: "", rest: pathname };
	const rest = pathname.slice(PUBLIC_PATH_PREFIX.length) || "/";
	return { prefix: "/public", rest: rest.startsWith("/") ? rest : `/${rest}` };
};

export const inferLanguageFromPathname = (pathname: string): string | null => {
	const { rest } = stripPublicPrefix(pathname);
	const match = rest.match(/_([A-Z]{2,3})\.html$/);
	if (!match?.[1]) return null;
	return normalizeMenuLanguage(match[1]);
};

export const buildLanguageRedirectPath = (
	pathname: string,
	selectedLang: string,
): string | null => {
	const selected = normalizeMenuLanguage(selectedLang);
	const { prefix, rest } = stripPublicPrefix(pathname);

	// Don't try to localize the menu page (there is no menu_IDN.html).
	if (rest === "/menu.html" || rest === "/menu") return null;

	// Don't try to localize the audio library (there is no audio-library_IDN.html
	// -- it's a single page with its own internal language dropdown).
	if (rest === "/audio-library.html" || rest === "/audio-library") return null;

	// Root -> index page redirect based on language
	if (rest === "/" || rest === "/index.html") {
		if (selected === DEFAULT_SITE_LANGUAGE) {
			return `${prefix}/`;
		}
		return `${prefix}/index_${selected}.html`;
	}

	if (!rest.endsWith(".html")) return null;

	const match = rest.match(/^(.*?)(?:_([A-Z]{2,3}))?\.html$/);
	if (!match) return null;

	const base = match[1]; // without ".html" and without "_XXX"
	const currentCode = match[2] ?? "";

	if (selected === DEFAULT_SITE_LANGUAGE) {
		if (!currentCode) return null;
		const targetRest = `${base}.html`;
		return targetRest === "/index.html" ? `${prefix}/` : `${prefix}${targetRest}`;
	}

	if (currentCode === selected) return null;
	return `${prefix}${base}_${selected}.html`;
};

const isThemeMode = (v: unknown): v is ThemeMode => v === "light" || v === "dark";
const isMenuFontFamily = (v: unknown): v is MenuFontFamily =>
	v === "sans" || v === "serif";

// Legacy (pre-popup) font persistence stored full CSS strings.
const LEGACY_FONT_SANS = '"Nunito Sans", "Lato", Arial, sans-serif';
const LEGACY_FONT_SERIF = "Georgia, Times New Roman";

export const getFontFamilyCss = (fontFamily: MenuFontFamily): string => {
	if (fontFamily === "serif") {
		return 'Georgia, "Times New Roman", Times, serif';
	}
	return LEGACY_FONT_SANS;
};

const parseMenuConfig = (raw: string): MenuConfig | null => {
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;

		const obj = parsed as Record<string, unknown>;

		if (typeof obj.language !== "string") return null;
		const language = normalizeMenuLanguage(obj.language);

		let theme: ThemeMode | null = null;
		if (isThemeMode(obj.theme)) {
			theme = obj.theme;
		} else if (typeof obj.darkMode === "boolean") {
			// Backward-compat: old config stored darkMode boolean.
			theme = obj.darkMode ? "dark" : "light";
		}

		let fontFamily: MenuFontFamily | null = null;
		if (isMenuFontFamily(obj.fontFamily)) {
			fontFamily = obj.fontFamily;
		} else if (typeof obj.fontFamily === "string") {
			// Backward-compat: old config stored a CSS string.
			if (obj.fontFamily === LEGACY_FONT_SANS) fontFamily = "sans";
			if (obj.fontFamily === LEGACY_FONT_SERIF) fontFamily = "serif";
		}

		if (!theme || !fontFamily) return null;

		return { language, theme, fontFamily };
	} catch {
		return null;
	}
};

export const writeMenuConfig = (config: MenuConfig): void => {
	localStorage.setItem(MENU_CONFIG_STORAGE_KEY, JSON.stringify(config));
};

export const readMenuConfig = (): MenuConfig => {
	const raw = localStorage.getItem(MENU_CONFIG_STORAGE_KEY);
	if (raw) {
		const parsed = parseMenuConfig(raw);
		if (parsed) {
			// Normalize/migrate any legacy shape to the canonical persisted shape.
			writeMenuConfig(parsed);
			return parsed;
		}
	}

	const systemTheme = detectPreferredThemeFromSystem();

	// If the user is visiting a localized HTML file (e.g. *_IDN.html), infer that language
	// instead of trusting <html lang="..."> which is often left as "en" in legacy pages.
	const inferredLanguage = inferLanguageFromPathname(window.location.pathname);

	const config: MenuConfig = {
		language:
			inferredLanguage ??
			normalizeMenuLanguage(document.documentElement.lang || DEFAULT_SITE_LANGUAGE),
		theme: systemTheme,
		fontFamily: "sans",
	};

	writeMenuConfig(config);
	return config;
};

export const updateMenuConfig = (patch: Partial<MenuConfig>): MenuConfig => {
	const current = readMenuConfig();

	const next: MenuConfig = {
		language:
			typeof patch.language === "string"
				? normalizeMenuLanguage(patch.language)
				: current.language,
		theme: isThemeMode(patch.theme) ? patch.theme : current.theme,
		fontFamily: isMenuFontFamily(patch.fontFamily)
			? patch.fontFamily
			: current.fontFamily,
	};

	writeMenuConfig(next);
	return next;
};

export const applyMenuConfigToDom = (config: MenuConfig): void => {
	// Language can safely be applied before body exists.
	if (config.language) document.documentElement.lang = config.language;

	// Font can be applied when body exists.
	if (document.body) document.body.style.fontFamily = getFontFamilyCss(config.fontFamily);
};

export const applyThemeToDom = (theme: ThemeMode): void => {
	const isDark = theme === "dark";
	document.documentElement.classList.toggle("dark-mode", isDark);
	document.documentElement.classList.toggle("light-mode", !isDark);
	document.body?.classList.toggle("dark-mode", isDark);
	document.body?.classList.toggle("light-mode", !isDark);
};